/**
 * Typed error model for API calls. UI never renders backend `message`/`details`
 * raw — it maps `code` to a friendly, privacy-safe message (spec §15).
 */
import type { ApiErrorBody } from './apiSchemas';

export class ApiHttpError extends Error {
  readonly status: number;
  /** Backend error code (e.g. LAST_AUTH_METHOD). May be 'UNKNOWN'. */
  readonly code: string;
  /** Correlation id, shown only behind a "support info" affordance (spec §15.3). */
  readonly requestId: string | null;

  constructor(status: number, code: string, requestId: string | null, message?: string) {
    // `message` is for developers/telemetry only, never shown raw to users.
    super(message ?? code);
    this.name = 'ApiHttpError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }

  static fromBody(status: number, body: ApiErrorBody): ApiHttpError {
    return new ApiHttpError(status, body.error.code, body.requestId, body.error.message);
  }
}

/** Network failure / timeout / non-JSON — no server code available. */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

export function isApiHttpError(err: unknown): err is ApiHttpError {
  return err instanceof ApiHttpError;
}

export function isUnauthorized(err: unknown): boolean {
  return isApiHttpError(err) && (err.status === 401 || err.status === 403);
}

/** True for the 401 case that should trigger global session-expiry handling. */
export function isSessionExpired(err: unknown): boolean {
  return isApiHttpError(err) && err.status === 401;
}
